import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Trash2, UserPlus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  documentService,
  type DocumentCategory,
  type FolderShareRole,
} from '@/services/api/documentService';
import { userService } from '@/services/api/dashboardService';
import { QUERY_KEYS } from '@/services/queryClient';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { errorMessage } from '../documentUi';

interface DocumentsShareDialogProps {
  open: boolean;
  folder: DocumentCategory | null;
  onClose: () => void;
}

const DocumentsShareDialog: React.FC<DocumentsShareDialogProps> = ({ open, folder, onClose }) => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useSnackbar();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<FolderShareRole>('viewer');

  const sharesQuery = useQuery({
    queryKey: QUERY_KEYS.documentFolderShares(folder?.id || ''),
    queryFn: () => documentService.listFolderShares(folder!.id),
    enabled: open && Boolean(folder?.id),
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'active-for-share'],
    queryFn: () => userService.getUsers('active', { pageSize: 200 }),
    enabled: open,
  });

  const sharedUserIds = useMemo(
    () => new Set((sharesQuery.data || []).map((s) => s.userId)),
    [sharesQuery.data]
  );

  const availableUsers = useMemo(
    () => (usersQuery.data || []).filter((u) => u.id && !sharedUserIds.has(u.id)),
    [usersQuery.data, sharedUserIds]
  );

  const addShare = useMutation({
    mutationFn: () => documentService.addFolderShare(folder!.id, userId, role),
    onSuccess: () => {
      setUserId('');
      setRole('viewer');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentFolderShares(folder!.id) });
      queryClient.invalidateQueries({ queryKey: ['documents', 'categories'] });
      showSuccess('Access granted for this folder');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  const removeShare = useMutation({
    mutationFn: (shareId: string) => documentService.removeFolderShare(folder!.id, shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentFolderShares(folder!.id) });
      queryClient.invalidateQueries({ queryKey: ['documents', 'categories'] });
      showSuccess('Access removed');
    },
    onError: (err) => showError(errorMessage(err)),
  });

  if (!folder) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share “{folder.name}”</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Folders are private by default. Share only this folder — the parent is not shared. Shared
          people can view files and upload / add Drive links in NestTask. Editor also gets edit access
          in Google Drive; Viewer is read-only in Drive. Parents may appear as a grey path, but they
          cannot open parent files.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
          <AppSelect
            label="User"
            value={userId}
            onChange={(e) => setUserId(String(e.target.value))}
            sx={{ flex: 1 }}
          >
            {availableUsers.map((u) => (
              <AppMenuItem key={u.id} value={u.id}>
                {u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                {u.email ? ` (${u.email})` : ''}
              </AppMenuItem>
            ))}
          </AppSelect>
          <AppSelect
            label="Permission"
            fullWidth={false}
            value={role}
            onChange={(e) => setRole(e.target.value as FolderShareRole)}
            formControlProps={{ sx: { width: 140, minWidth: 140 } }}
          >
            <AppMenuItem value="viewer">Viewer</AppMenuItem>
            <AppMenuItem value="editor">Editor</AppMenuItem>
          </AppSelect>
          <Button
            variant="contained"
            startIcon={<UserPlus size={16} />}
            disabled={!userId || addShare.isPending}
            onClick={() => addShare.mutate()}
            sx={{ mt: { sm: 0.5 } }}
          >
            Add
          </Button>
        </Stack>

        {sharesQuery.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(sharesQuery.error)}</Alert>
        )}

        <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
          People with access
        </Typography>
        {(sharesQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No one has access yet — this folder is private until you share it.
          </Typography>
        ) : (
          <List dense disablePadding>
            {(sharesQuery.data || []).map((share) => (
              <ListItem
                key={share.id}
                sx={{ px: 0 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    disabled={removeShare.isPending}
                    onClick={() => removeShare.mutate(share.id)}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={share.userName}
                  secondary={`${share.email} · ${share.role === 'editor' ? 'Editor' : 'Viewer'}`}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Box sx={{ height: 8 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentsShareDialog;
